import React, { useRef, useLayoutEffect, useState } from "react";
import "./WorkShowcase.css";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import work1 from "../assets/work1.mp4";
import work2 from "../assets/work2.mp4";
import work3 from "../assets/work3.mp4";
import work4 from "../assets/work4.mp4";
import work5 from "../assets/work5.mp4";
import work6 from "../assets/work6.mp4";

gsap.registerPlugin(ScrollTrigger);

// =====================================
// Portfolio Data
// =====================================

const works = [
  {
    id: 1,
    title: "Project One",
    category: "Brand Identity",
    video: work1,
  },
  {
    id: 2,
    title: "Project Two",
    category: "Editorial Design",
    video: work2,
  },
  {
    id: 3,
    title: "Project Three",
    category: "Creative Direction",
    video: work3,
  },
  {
    id: 4,
    title: "Project Four",
    category: "Packaging",
    video: work4,
  },
  {
    id: 5,
    title: "Project Five",
    category: "Motion Design",
    video: work5,
  },
  {
    id: 6,
    title: "Project Six",
    category: "Digital Campaign",
    video: work6,
  },
];

export default function WorkShowcase() {

  // ===============================
  // Refs
  // ===============================

  const sectionRef = useRef(null);

  const headingRef = useRef(null);

  const wrapperRef = useRef(null);

  const cardsRef = useRef([]);

  const videosRef = useRef([]);

  // ===============================
  // State
  // ===============================

  const [activeVideo, setActiveVideo] = useState(-1);

  // ===============================
  // Helper Function
  // ===============================

  const addCardRef = (el, index) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current[index] = el;
    }
  };

  const addVideoRef = (el, index) => {
    if (el && !videosRef.current.includes(el)) {
      videosRef.current[index] = el;
    }
  };
    // =====================================
  // GSAP Scroll Animations
  // =====================================

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const wrapper = wrapperRef.current;

    if (!section || !wrapper) return;

    const ctx = gsap.context(() => {

      // -----------------------------------
      // Heading Animation
      // -----------------------------------

      gsap.from(headingRef.current, {
        y: 120,
        opacity: 0,
        duration: 1.2,
        ease: "power4.out",
      });

      // -----------------------------------
      // Cards Reveal
      // -----------------------------------

      gsap.from(cardsRef.current, {
        opacity: 0,
        y: 110,
        scale: 0.9,
        stagger: 0.10,
        duration: 1.2,
        ease: "power4.out",
        scrollTrigger: {
          trigger: section,
          start: "top 60%",
        },
      });

      // -----------------------------------
      // Calculate Scroll Width
      // -----------------------------------

     const getDistance = () => {
  const lastCard = cardsRef.current[cardsRef.current.length - 1];

  if (!lastCard) return 0;

  return (
    lastCard.offsetLeft -
    (window.innerWidth - lastCard.offsetWidth) / 2
   
  );
};
      // -----------------------------------
      // Horizontal Scroll
      // -----------------------------------

   const horizontalTween = gsap.to(wrapper, {
  x: () => -getDistance(),
  ease: "none",
  scrollTrigger: {
    trigger: section,
    start: "top top",
    end: () => `+=${getDistance()}`,
    scrub: true,
    pin: true,
    invalidateOnRefresh: true,
    anticipatePin: 1,
  },
});
      // -----------------------------------
      // Individual Card Animations
      // -----------------------------------

      cardsRef.current.forEach((card, index) => {

        if (!card) return;

        const video = card.querySelector("video");

        // Floating animation
        gsap.to(card, {
          y: index % 2 === 0 ? -25 : 25,
          repeat: -1,
          yoyo: true,
          duration: 3 + index * 0.2,
          ease: "sine.inOut",
        });

        // Video parallax
        if (video) {
          gsap.to(video, {
            y: -60,
            scale: 1.12,
            ease: "none",
            scrollTrigger: {
              trigger: card,
              containerAnimation: horizontalTween,
              start: "left right",
              end: "right left",
              scrub: true,
            },
          });
        }

      });

      // -----------------------------------
      // Refresh on Resize
      // -----------------------------------

      const handleResize = () => {
        ScrollTrigger.refresh();
      };

      window.addEventListener("resize", handleResize);

      ScrollTrigger.refresh();

      return () => {
        window.removeEventListener("resize", handleResize);
      };

    }, section);

    return () => ctx.revert();

  }, []);
    // =====================================
  // Toggle Audio
  // =====================================

  const toggleVideoAudio = (index) => {
    videosRef.current.forEach((video, i) => {
      if (!video) return;

      if (i === index) {
        const enableSound = video.muted;

        // Mute every video first
        videosRef.current.forEach((v) => {
          if (!v) return;
          v.muted = true;
          v.volume = 0;
        });

        if (enableSound) {
          video.muted = false;
          video.volume = 1;
          setActiveVideo(index);
        } else {
          video.muted = true;
          video.volume = 0;
          setActiveVideo(-1);
        }
      }
    });
  };

  // =====================================
  // Auto Play Videos
  // =====================================

  useLayoutEffect(() => {
    videosRef.current.forEach((video) => {
      if (!video) return;

      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      const promise = video.play();

      if (promise !== undefined) {
        promise.catch(() => {});
      }
    });
  }, []);

  // =====================================
  // Mouse Move Tilt Effect
  // =====================================

  const handleMouseMove = (e) => {
    const card = e.currentTarget;

    const rect = card.getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const rotateY = ((x - rect.width / 2) / rect.width) * 10;
    const rotateX = -((y - rect.height / 2) / rect.height) * 10;

    gsap.to(card, {
      rotateX,
      rotateY,
      scale: 1.04,
      duration: 0.35,
      ease: "power3.out",
      transformPerspective: 1200,
      transformOrigin: "center center",
    });

    const video = card.querySelector("video");

    if (video) {
      gsap.to(video, {
        scale: 1.08,
        duration: 0.35,
        ease: "power3.out",
      });
    }
  };

  // =====================================
  // Mouse Leave
  // =====================================

  const handleMouseLeave = (e) => {
    const card = e.currentTarget;

    gsap.to(card, {
      rotateX: 0,
      rotateY: 0,
      scale: 1,
      duration: 0.5,
      ease: "power3.out",
    });

    const video = card.querySelector("video");

    if (video) {
      gsap.to(video, {
        scale: 1,
        duration: 0.5,
        ease: "power3.out",
      });
    }
  };

  // =====================================
  // Play / Pause On Hover (Optional)
  // =====================================

  const handleMouseEnter = (index) => {
    const video = videosRef.current[index];

    if (!video) return;

    video.play().catch(() => {});
  };
  return (
  <section id="company-profile" className="work-showcase" ref={sectionRef} >
    {/* ===========================
        Heading
    ============================ */}

    <div className="showcase-heading" ref={headingRef}>
      <h1>
        WORK
        <br />
        SHOWCASE
         <br />
          <br />
           <br />
      </h1>
    </div>

    {/* ===========================
        Cards Wrapper
    ============================ */}

    <div className="cards-wrapper" ref={wrapperRef}>
      {works.map((work, index) => (
        <div
          key={work.id}
          className={`work-card ${
            activeVideo === index ? "active-video" : ""
          }`}
          ref={(el) => addCardRef(el, index)}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onMouseEnter={() => handleMouseEnter(index)}
          onClick={() => toggleVideoAudio(index)}
        >
          {/* =======================
              Video
          ======================== */}

          <div className="image-wrapper">
            <video
              ref={(el) => addVideoRef(el, index)}
              className="work-video"
              src={work.video}
              muted
              autoPlay
              loop
              playsInline
              preload="metadata"
            />
          </div>

          {/* =======================
              Overlay
          ======================== */}

          <div className="overlay">
            <div className="overlay-top">
              <span className="category">{work.category}</span>
            </div>

            <div className="overlay-bottom">
              <h3>{work.title}</h3>

              <button
                className="sound-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleVideoAudio(index);
                }}
              >
                {activeVideo === index ? "🔊 Sound On" : "🔇 Sound Off"}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);
}